import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_BASE, getAuthHeaders } from './client';

// ==================== TYPES ====================

export interface ResumeContent {
  id: string;
  name: string;
  type: 'masterTemplate' | 'skills' | 'experience' | 'fullResume' | 'education' | 'notes';
  content_markdown: string;
  description?: string;
  is_active: boolean;
  is_default: boolean;
  include_by_default: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ResumeVersion {
  id: string;
  name: string;
  content_markdown: string;
  used_content_ids: string[];
  generated_for_job_id?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateResumeContentRequest {
  name: string;
  type: 'masterTemplate' | 'skills' | 'experience' | 'fullResume' | 'education' | 'notes';
  content_markdown: string;
  description?: string;
  sort_order?: number;
  is_default?: boolean;
  include_by_default?: boolean;
}

export interface UpdateResumeContentRequest extends Partial<CreateResumeContentRequest> {
  is_active?: boolean;
}

export interface CompileResumeRequest {
  name: string;
  masterTemplateId: string;
  contentIds: string[];
  generatedForJobId?: string;
}

// ==================== QUERIES ====================

/**
 * Fetch all resume content items
 */
export const useResumeContent = () => {
  return useQuery({
    queryKey: ['resumes', 'content'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/resumes/content`, { headers });
      if (!res.ok) throw new Error('Failed to fetch resume content');
      return res.json() as Promise<ResumeContent[]>;
    },
  });
};

/**
 * Fetch a specific resume content item by ID
 */
export const useResumeContentItem = (id: string | undefined) => {
  return useQuery({
    queryKey: ['resumes', 'content', id],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/resumes/content/${id}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch resume content');
      return res.json() as Promise<ResumeContent>;
    },
    enabled: !!id,
  });
};

/**
 * Fetch all resume versions
 */
export const useResumeVersions = (jobId?: string) => {
  return useQuery({
    queryKey: ['resumes', 'versions', jobId],
    queryFn: async () => {
      const url = new URL(`${API_BASE}/resumes/versions`);
      if (jobId) {
        url.searchParams.append('jobId', jobId);
      }
      const headers = await getAuthHeaders();
      const res = await fetch(url.toString(), { headers });
      if (!res.ok) throw new Error('Failed to fetch resume versions');
      return res.json() as Promise<ResumeVersion[]>;
    },
  });
};

/**
 * Fetch a specific resume version by ID
 */
export const useResumeVersion = (id: string | undefined) => {
  return useQuery({
    queryKey: ['resumes', 'versions', id],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/resumes/versions/${id}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch resume version');
      return res.json() as Promise<ResumeVersion>;
    },
    enabled: !!id,
  });
};

// ==================== MUTATIONS ====================

/**
 * Create a new resume content item
 */
export const useCreateResumeContent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateResumeContentRequest) => {
      console.log('[API] Creating resume content:', data);
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/resumes/content`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        console.error('[API] Error response:', error);
        throw new Error(error.error || 'Failed to create resume content');
      }
      const result = await res.json();
      console.log('[API] Create success:', result);
      return result as Promise<ResumeContent>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resumes', 'content'] });
    },
  });
};

/**
 * Update an existing resume content item
 */
export const useUpdateResumeContent = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateResumeContentRequest) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/resumes/content/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update resume content');
      }
      return res.json() as Promise<ResumeContent>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resumes', 'content'] });
      queryClient.invalidateQueries({ queryKey: ['resumes', 'content', id] });
    },
  });
};

/**
 * Delete a resume content item
 */
export const useDeleteResumeContent = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/resumes/content/${id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to delete resume content');
      }
      return res.json() as Promise<{ success: boolean; id: string }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resumes', 'content'] });
    },
  });
};

/**
 * Compile a resume from selected content pieces
 */
export const useCompileResume = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CompileResumeRequest) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/resumes/compile`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to compile resume');
      }
      return res.json() as Promise<ResumeVersion>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resumes', 'versions'] });
    },
  });
};
